import {
  BOOKING_DAILY_PRICING_TYPE_ID,
  BOOKING_HOURLY_PRICING_TYPE_ID,
  BOOKING_MONTHLY_PRICING_TYPE_ID,
  DEFAULT_BOOKING_DAILY_PRICE_ILS,
  DEFAULT_BOOKING_HOURLY_PRICE_ILS,
} from '../constants/bookingQuickDefaults';
import {
  resolveAssetBookingPricingOptions,
  resolveListingHourlyDailyPrices,
  type AssetBookingPricingOption,
} from './assetBookingPricingOptions';

export type BookingHourDayPrices = {
  hourly: number;
  daily: number;
  hourlyPricingTypeId: number;
  dailyPricingTypeId: number;
};

export type BookingQuickPrices = BookingHourDayPrices & {
  monthly: number | null;
  monthlyPricingTypeId: number;
};

/** Hourly / daily rates for booking — uses owner prices when set, else defaults. */
export function resolveBookingHourDayPrices(asset: Record<string, unknown>): BookingHourDayPrices {
  const { hourly, daily } = resolveListingHourlyDailyPrices(asset);
  return {
    hourly: hourly ?? DEFAULT_BOOKING_HOURLY_PRICE_ILS,
    daily: daily ?? DEFAULT_BOOKING_DAILY_PRICE_ILS,
    hourlyPricingTypeId: BOOKING_HOURLY_PRICING_TYPE_ID,
    dailyPricingTypeId: BOOKING_DAILY_PRICING_TYPE_ID,
  };
}

/** Quick booking rates — monthly is included only when the owner set a monthly price. */
export function resolveBookingQuickPrices(asset: Record<string, unknown>): BookingQuickPrices {
  const { hourly, daily, monthly } = resolveListingHourlyDailyPrices(asset);
  return {
    hourly: hourly ?? DEFAULT_BOOKING_HOURLY_PRICE_ILS,
    daily: daily ?? DEFAULT_BOOKING_DAILY_PRICE_ILS,
    monthly,
    hourlyPricingTypeId: BOOKING_HOURLY_PRICING_TYPE_ID,
    dailyPricingTypeId: BOOKING_DAILY_PRICING_TYPE_ID,
    monthlyPricingTypeId: BOOKING_MONTHLY_PRICING_TYPE_ID,
  };
}

/** Booking UI supports hour, day, and month (month only when owner priced it). */
export function filterConsumerBookingPricingOptions(
  options: AssetBookingPricingOption[],
): AssetBookingPricingOption[] {
  return options.filter(
    (option) => option.kind === 'hour' || option.kind === 'day' || option.kind === 'month',
  );
}

/** @deprecated Use {@link filterConsumerBookingPricingOptions}. */
export function filterHourDayBookingPricingOptions(
  options: AssetBookingPricingOption[],
): AssetBookingPricingOption[] {
  return filterConsumerBookingPricingOptions(options);
}

/**
 * Hour, day, and optional month options for booking UI.
 * Uses owner prices when set; otherwise 50 / 400 ILS defaults for hour and day.
 */
export function resolveHourDayBookingPricingOptions(
  asset: Record<string, unknown>,
  catalogRows: Record<string, unknown>[],
): AssetBookingPricingOption[] {
  const filtered = filterConsumerBookingPricingOptions(
    resolveAssetBookingPricingOptions(asset, catalogRows),
  );
  const prices = resolveBookingQuickPrices(asset);
  const hourFromAsset = filtered.find((o) => o.kind === 'hour');
  const dayFromAsset = filtered.find((o) => o.kind === 'day');
  const monthFromAsset = filtered.find((o) => o.kind === 'month');
  const out: AssetBookingPricingOption[] = [
    hourFromAsset ?? {
      kind: 'hour' as const,
      pricingTypeId: prices.hourlyPricingTypeId,
      label: 'Hour',
      price: prices.hourly,
    },
    dayFromAsset ?? {
      kind: 'day' as const,
      pricingTypeId: prices.dailyPricingTypeId,
      label: 'Day',
      price: prices.daily,
    },
  ];
  if (prices.monthly !== null) {
    out.push(
      monthFromAsset ?? {
        kind: 'month' as const,
        pricingTypeId: prices.monthlyPricingTypeId,
        label: 'Month',
        price: prices.monthly,
      },
    );
  }
  return out;
}
